/** @jsxImportSource @emotion/react */
import type { CSSProperties } from 'react';
import { memo, useCallback, useMemo, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { ChevronUp } from 'react-feather';
import { useIdentifyBrowser } from '@trinity/chat-components/hooks/useIdentifyBrowser';
import { useOnResize } from '@trinity/chat-components/hooks/useOnResize';
import { useToggle } from '@trinity/chat-components/hooks/useToggle';

export type SeeMoreProps = {
  lineHeight: number;
  maxLines: number;
};

type Props = {
  children: React.ReactNode;
  className?: string;
  css?: CSSOverrides;
  expanderClassName?: string;
  CollapserComp?: React.ComponentType<Omit<ExpanderProps, 'text'>>;
  ExpanderComp?: React.ComponentType<Omit<ExpanderProps, 'text'>>;
} & SeeMoreProps;

type ExpanderProps = {
  className?: string;
  onClick: () => unknown;
  text: string;
};

export const useTruncatedTextContainer = ({ lineHeight, maxLines }: SeeMoreProps) => {
  const [collapsed, toggleCollapsed] = useToggle(true);
  const [hasOverflow, setOverflow] = useState<boolean>(null);
  const { firefox } = useIdentifyBrowser();
  const internalRef = useRef<HTMLDivElement>(undefined);
  useOnResize(useCallback(() => setOverflow(null), []));

  const calculateOverflow = useCallback(() => {
    const node = internalRef.current;
    if (!node) return;

    // NOTE: Firefox seems to render our divs with 1px more than it should be.
    //       Give it a small allowance before we consider it to be overflowing
    const fudgeFactor = firefox ? 1 : 0;

    const maxHeight = lineHeight * maxLines;

    setOverflow(node.scrollHeight > (maxHeight + fudgeFactor));
  }, [setOverflow, firefox, lineHeight, maxLines]);

  const ref = (node: HTMLDivElement) => {
    internalRef.current = node;
    calculateOverflow();

    if (node) {
      const observer = new ResizeObserver(() => calculateOverflow());
      observer.observe(node);
      return () => observer.disconnect();
    }
  };

  const style: CSSProperties = useMemo(() => ({
    lineHeight: `${lineHeight}px`,
    maxHeight: collapsed && `${lineHeight * maxLines}px`,
    overflow: hasOverflow && collapsed ? 'hidden' : '',
  }), [
    collapsed,
    hasOverflow,
    lineHeight,
    maxLines,
  ]);

  return {
    collapsed,
    hasOverflow,
    toggleCollapsed,
    props: {
      ref,
      style,
    },
  } as const;
};

export const SeeMore = memo(({
  css = {},
  lineHeight,
  maxLines,
  CollapserComp,
  ExpanderComp = Expander,
  ...props
}: Props) => {

  const container = useTruncatedTextContainer({
    lineHeight,
    maxLines,
  });

  if (!props.children) return null;

  return (
    <Root className={props.className} {...container.props}>
      {props.children}
      {container.collapsed && container.hasOverflow &&
        <ExpanderComp
          className={props.expanderClassName}
          onClick={container.toggleCollapsed} />
      }
      {!container.collapsed && CollapserComp &&
        <CollapserComp
          css={css}
          className={props.expanderClassName}
          onClick={container.toggleCollapsed} />
      }
    </Root>
  );
});

export const Collapser = memo((props: Omit<ExpanderProps, 'text'>) => (
  <BaseExpander
    {...props}
    text="See less" />
));

type CollapserCompactProps = {
  css?: CSSOverrides;
} & Omit<ExpanderProps, 'text'>;

export const CollapserCompact = memo(({ css = {}, ...props }: CollapserCompactProps) => (
  <CompactLess
    css={css}
    {...props}>
    <ChevronUp />
  </CompactLess>
));

export const Expander = memo((props: Omit<ExpanderProps, 'text'>) => (
  <BaseExpander
    {...props}
    text="...See more" />
));

const BaseExpander = memo(({ className, onClick, text }: ExpanderProps) => {

  return (
    <More
      className={className}
      onClick={onClick}>
      {text}
    </More>
  );
});

export default SeeMore;

const Root = styled.div(({ theme }) => ({
  position: 'relative',

  [`&:hover > $${More}`]: {
    color: theme.palette.hyperlink,
    textDecoration: 'underline',
  },
}));

const More = styled.div(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  cursor: 'pointer',
  color: theme.palette.gray.main,
  fontSize: 14,
  backgroundColor: theme.palette.white.main,
}));

const CompactLess = styled(More)({
  fontStyle: 'normal',
  fontSize: 16,
});